import { NutritionSource, NutritionalValues, ProductNutritionalInfo } from '../../../components/shared/nutrition/types';
import { RecipeV2Detail } from '../types';

/** Demo Mode only - deterministic, recipe-derived nutrition values (see NutritionTab.Nutrition).
 * No backend persistence or real nutrition calculation exists yet, so figures are generated
 * from the recipe's id/portion size rather than left empty, to showcase the Nutritional Info
 * tab with plausible-looking numbers. Same recipe always yields the same values.
 * lazy: seeded pseudo-random within realistic per-100g ranges, not a real recipe nutrition
 * calculation (ingredient-weighted) - upgrade to a real calculation engine if this needs to
 * drive actual labelling/compliance decisions. */
const seededRandom = (seed: number) => {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
};

const inRange = (seed: number, min: number, max: number, decimals = 1) => {
  const value = min + seededRandom(seed) * (max - min);
  return Number(value.toFixed(decimals));
};

export const getMockRecipeNutrition = (recipe: RecipeV2Detail): ProductNutritionalInfo => {
  const seed = recipe.id || 1;

  const per100g: NutritionalValues = {
    energyKcal: inRange(seed * 1.1, 90, 320, 0),
    fat: inRange(seed * 1.2, 2, 18, 1),
    saturates: inRange(seed * 1.3, 0.5, 7, 1),
    carbohydrate: inRange(seed * 1.4, 5, 40, 1),
    sugars: inRange(seed * 1.5, 1, 12, 1),
    fibre: inRange(seed * 1.6, 0.5, 6, 1),
    protein: inRange(seed * 1.7, 4, 30, 1),
    salt: inRange(seed * 1.8, 0.1, 1.8, 2),
  };
  per100g.energyKj = Math.round((per100g.energyKcal ?? 0) * 4.184);

  const portionGrams = recipe.portionSize?.unit?.toLowerCase() === 'g'
    ? recipe.portionSize.quantity
    : 250;
  const scale = portionGrams / 100;
  const perServing: NutritionalValues = Object.fromEntries(
    Object.entries(per100g).map(([key, value]) => [key, Number((value * scale).toFixed(key === 'salt' ? 2 : 1))]),
  );

  return {
    source: NutritionSource.Recipe,
    servingSize: `${portionGrams}g`,
    per100g,
    perServing,
  };
};
